import { Injectable } from '@angular/core';
import { amorphicStatic } from '@haventech/amorphic';
import { MailToInfo, InviteEmailType, EmailProps } from '../../types/Types';
import { FormatterService } from '../../../../../coverpath-common/angular/services/formatter/formatter.service';
import { Utils } from '../../../../../common/js/Utils';
import { Product } from '../../../../../common/js/static/Product';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

const EMAIL_SOLICITATION_DISCLAIMER = `Please Note: MassMutual is licensed to sell insurance products in all 50 states of the United States (including the District of Columbia) and Puerto Rico (collectively, the "US"). By completing and submitting this application, you are confirming that you are currently located in the US. If you are not located in the US, please do not continue with this application. If we determine that you were located outside of the US at the time you completed this application, you understand and agree that we will not issue a policy to you and/or we may rescind any coverage issued in connection with this application.`;

@Injectable()
export class MailToService {
  private static formatterService: FormatterService = new FormatterService();

  private static advisorBCCEmail: string = 'advisors@coverpath.com';

  public static openMailToEmail(mailToInfo: MailToInfo): void {
    const ua = navigator.userAgent;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua)) {
      // mobile devices are known to have built in email clients, so opening in the same tab is safe
      window.open(MailToService.buildURL(mailToInfo), '_self');
    } else {
      // laptops and desktops may or may not have a default email client, so opening in a new tab is, although not ideal, a practical choice.
      window.open(MailToService.buildURL(mailToInfo), '_blank');
    }
  }

  protected static buildURL(mailToInfo: MailToInfo): string {
    let url = 'mailto:';
    url += mailToInfo.clientEmail || '';
    url += `?bcc=${MailToService.advisorBCCEmail}`;
    url += '&subject=' + encodeURIComponent(MailToService.getEmailSubject(mailToInfo));
    url += '&body=' + encodeURIComponent(MailToService.getEmailBody(mailToInfo));
    return url;
  }

  public mailTo({ to, body, subject }: EmailProps): void {
    let url = 'mailto:';
    url += to || '';
    url += `?bcc=${MailToService.advisorBCCEmail}`;
    url += '&subject=' + encodeURIComponent(subject);
    url += '&body=' + encodeURIComponent(body);
    return this.baseMailTo(url);
  }

  public mailToNoBcc({ to, body, subject }: EmailProps): void {
    let url = 'mailto:';
    url += to || '';
    url += '?subject=' + encodeURIComponent(subject);
    url += '&body=' + encodeURIComponent(body);
    return this.baseMailTo(url);
  }

  public baseMailTo(url: string): void {
    const ua = navigator.userAgent;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua)) {
      // mobile devices are known to have built in email clients, so opening in the same tab is safe
      window.open(url, '_self');
    } else {
      // laptops and desktops may or may not have a default email client, so opening in a new tab is, although not ideal, a practical choice.
      window.open(url, '_blank');
    }
  }

  public static getEmailSubject(mailToInfo: MailToInfo): string {
    let subject = '';
    const insuredNameString = ` for ${mailToInfo.insuredFullName}`;
    switch (mailToInfo.invitationType) {
      case InviteEmailType.GENERIC:
      case InviteEmailType.PERSONALIZED:
        subject = 'Get Covered Today';
        break;
      case InviteEmailType.QUOTE:
        subject = `Your Life Insurance Quote${mailToInfo.ownerEqualsInsured ? '' : insuredNameString}`;
        break;
      case InviteEmailType.ADVISOR_ASSISTED:
        subject = `Your application${mailToInfo.ownerEqualsInsured ? '' : insuredNameString} is almost ready to go`;
        break;
      case InviteEmailType.ADVISOR_EDIT:
        subject = 'Updates to your application';
        break;
      case InviteEmailType.CONVERSION:
        subject = 'Your term life conversion';
        break;
      default:
        UILoggerSingleton.getInstance().info(
          {
            module: 'MailToService',
            function: 'getEmailSubject',
            category: UICategory.request,

            data: {
              invitationType: mailToInfo.invitationType,
            },
          },
          'Invalid email invitation type'
        );
    }
    return subject;
  }

  public static getEmailBody(mailToInfo: MailToInfo): string {
    let body = '';
    switch (mailToInfo.invitationType) {
      case InviteEmailType.GENERIC:
        body = MailToService.getGenericEmailBody(mailToInfo);
        break;
      case InviteEmailType.PERSONALIZED:
        body = MailToService.getPersonalizedLinkEmailBody(mailToInfo);
        break;
      case InviteEmailType.QUOTE:
        body = MailToService.getQuoteEmailBody(mailToInfo);
        break;
      case InviteEmailType.ADVISOR_ASSISTED:
        body = MailToService.getAdvisorAssistedApplicationEmailBody(mailToInfo);
        break;
      case InviteEmailType.ADVISOR_EDIT:
        body = MailToService.getAdvisorEditEmailBody(mailToInfo);
        break;
      case InviteEmailType.CONVERSION:
        body = MailToService.getConversionEmailBody(mailToInfo);
        break;
      default:
        UILoggerSingleton.getInstance().info(
          {
            module: 'MailToService',
            function: 'getEmailBody',
            category: UICategory.request,
            data: {
              invitationType: mailToInfo.invitationType,
            },
          },
          'Invalid email invitation type'
        );
    }
    return body;
  }

  protected static getGenericEmailBody(mailToInfo: MailToInfo): string {
    return (
      "Getting life insurance shouldn't be hard.\n\nClick here to get an instant quote from MassMutual:\n" +
      mailToInfo.inviteLink +
      '\n\nCongratulations on taking the first step towards getting covered.\n\nTalk to you soon,\n' +
      Utils.capitalizeFirstLetter(mailToInfo.agentFirstName) +
      `\n\n\n\n${EMAIL_SOLICITATION_DISCLAIMER}`
    );
  }

  //Spacing is intentional, please do not change line breaks
  protected static getPersonalizedLinkEmailBody(mailToInfo: MailToInfo): string {
    return (
      'Hi ' +
      Utils.capitalizeFirstLetter(mailToInfo.clientFirstName) +
      ",\n\nGetting life insurance shouldn't be hard.\n\nClick here to get an instant quote from MassMutual:\n" +
      mailToInfo.inviteLink +
      '\n\nCongratulations on taking the first step towards getting covered.\n\nTalk to you soon,\n' +
      Utils.capitalizeFirstLetter(mailToInfo.agentFirstName) +
      `\n\n\n\n${EMAIL_SOLICITATION_DISCLAIMER}`
    );
  }

  protected static getQuoteEmailBody(mailToInfo: MailToInfo): string {
    return (
      'Hi ' +
      Utils.capitalizeFirstLetter(mailToInfo.clientFirstName) +
      ",\n\nGetting life insurance shouldn't be hard. MassMutual is making it easy, see the details of the quote I prepared for you below." +
      `\n\nClick here to see your quote${
        mailToInfo.ownerEqualsInsured ? '' : ` for ${mailToInfo.insuredFullName}`
      }:\n` +
      mailToInfo.inviteLink +
      '\n\nYou can complete your application entirely online. Please let me know if I can help with anything.\n\nTalk to you soon,\n' +
      Utils.capitalizeFirstLetter(mailToInfo.agentFirstName) +
      `\n\n\n\n${EMAIL_SOLICITATION_DISCLAIMER}`
    );
  }

  protected static getAdvisorAssistedApplicationEmailBody(mailToInfo: MailToInfo): string {
    const termFaceAmount = mailToInfo.termFaceAmount
      ? MailToService.formatterService.format('currency', mailToInfo.termFaceAmount)
      : undefined;

    const wholeFaceAmount = mailToInfo.wholeFaceAmount
      ? MailToService.formatterService.format('currency', mailToInfo.wholeFaceAmount)
      : undefined;

    // Want to display 'Annually Renewable Term' instead of 'Policy Term: 1 Years'
    const termText =
      mailToInfo.term && mailToInfo.term === 1
        ? Product.products.CASTerm.artDescription
        : 'Policy Term: ' + mailToInfo.term + ' Years';

    let body = `Hi ${Utils.capitalizeFirstLetter(
      mailToInfo.clientFirstName
    )},\n\nIt shouldn't take long for you to review and complete your application${
      mailToInfo.ownerEqualsInsured ? '' : ` for ${mailToInfo.insuredFullName}`
    }.\n\n`;

    if (mailToInfo.isJointApp) {
      body += `Whole Life Policy Amount: ${wholeFaceAmount}`;
      if (mailToInfo.showPremiumToClient) {
        body += `\nYour Estimated ${mailToInfo.wholePaymentFrequency} Cost: ${Utils.formatCurrency(
          mailToInfo.wholePremium
        )}`;
      }
      body += '\n\n';
      body += `Convertible Policy Amount: ${termFaceAmount}\n${termText}`;
      if (mailToInfo.showPremiumToClient) {
        body += `\nYour Estimated ${mailToInfo.termPaymentFrequency} Cost: ${Utils.formatCurrency(
          mailToInfo.termPremium
        )}`;
      }
    } else if (mailToInfo.productType.match(/term/i)) {
      body += `Convertible Policy Amount: ${termFaceAmount}\n${termText}`;
      if (mailToInfo.showPremiumToClient) {
        body += `\nYour Estimated ${mailToInfo.termPaymentFrequency} Cost: ${Utils.formatCurrency(
          mailToInfo.termPremium
        )}`;
      }
    } else {
      // whole
      body += `Whole Life Policy Amount: ${wholeFaceAmount}`;
      if (mailToInfo.showPremiumToClient) {
        body += `\nYour Estimated ${mailToInfo.wholePaymentFrequency} Cost: ${Utils.formatCurrency(
          mailToInfo.wholePremium
        )}`;
      }
    }

    body += `\n\nFinish your application now:\n${mailToInfo.inviteLink}
      \nPlease let me know if I can help.
      \nTalk to you soon,\n${Utils.capitalizeFirstLetter(mailToInfo.agentFirstName)}`;

    body += `\n\n\n\n${EMAIL_SOLICITATION_DISCLAIMER}`;
    return body;
  }

  protected static getAdvisorEditEmailBody(mailToInfo: MailToInfo): string {
    const termFaceAmount = mailToInfo.termFaceAmount
      ? MailToService.formatterService.format('currency', mailToInfo.termFaceAmount)
      : undefined;

    const wholeFaceAmount = mailToInfo.wholeFaceAmount
      ? MailToService.formatterService.format('currency', mailToInfo.wholeFaceAmount)
      : undefined;

    // Want to display 'Annually Renewable Term' instead of 'Policy Term: 1 Years'
    const termText =
      mailToInfo.term && mailToInfo.term === 1
        ? Product.products.CASTerm.artDescription
        : 'Policy Term: ' + mailToInfo.term + ' Years';

    let body = `Hi ${Utils.capitalizeFirstLetter(
      mailToInfo.clientFirstName
    )},\n\nI made some updates to your application. You can see the latest here:\n\n${
      mailToInfo.inviteLink
    }\n\nInsured: ${mailToInfo.insuredFullName}\n\n`;

    if (mailToInfo.isJointApp) {
      body += `Whole Life ${mailToInfo.wholeTerm}\n`;

      body += `Face Amount: ${wholeFaceAmount}`;

      body += '\n\nConvertible Term\n';
      body += `Face Amount: ${termFaceAmount}\n${termText}`;
    } else if (mailToInfo.productType.match(/term/i)) {
      body += 'Convertible Term\n';
      body += `Face Amount: ${termFaceAmount}\n${termText}`;
    } else {
      body += `Whole Life ${mailToInfo.wholeTerm}\n`;
      body += `Face Amount: ${wholeFaceAmount}\n`;
    }

    body += `\n\n-${Utils.capitalizeFirstLetter(mailToInfo.agentFirstName)}`;

    body += `\n\n\n\n${EMAIL_SOLICITATION_DISCLAIMER}`;
    return body;
  }

  protected static getConversionEmailBody(mailToInfo: MailToInfo): string {
    return (
      'Hi ' +
      Utils.capitalizeFirstLetter(mailToInfo.clientFirstName) +
      ',\n\nI’ve prepared your application for a conversion from term to whole life. You can complete the rest of the process online: review the conversion, then provide your payment information and sign.' +
      `\n\nClick here to see your application:\n` +
      mailToInfo.inviteLink +
      '\n\nPlease let me know if I can help with anything.\n' +
      '\nTalk to you soon,\n' +
      Utils.capitalizeFirstLetter(mailToInfo.agentFirstName)
    );
  }
}
